import React, { useMemo, useRef, useState } from 'react';
import { BookingState } from '../../modules/booking-state/BookingState';
import { Dealer } from '../../modules/entities/Entities';
import { SelectDealerPageContext } from './SelectDealerPageContext';
import { SelectDealerPageFormData } from './SelectDealerPageFormData';
import { withPageContext } from '../../modules/contexts/WithPageContext';
import { useViewProvider } from '../../modules/contexts/view-provider-context/ViewProvider';
import { FormRef } from '../../modules/forms/Form';
import { Wizard, WizardNavigate } from '../../modules/wizard/Wizard';
import { Steps } from '../../modules/wizard/Steps';
import { usePagination } from 'react-use-pagination';
import { useFuzzy } from 'react-use-fuzzy';
import { useGetDealers } from '../../modules/booking-api/BookingApi';
import { useBookingContext } from '../../modules/booking-state/UseBookingContext';
import { SelectDealerPageViewProps } from './SelectDealerPageViewProps';
import { useModalManager } from "../../modules/contexts/modal-manager-context/ModalManagerProvider";
import deepEqual from "deep-equal";
import { resetSelectServiceDatePageContext } from "../select-service-date-page/SelectServiceDatePage";
import { ModalContent } from "../../modules/modal/ModalContent";

const dealersPagination = 10;
const minCharsToSearching = 3;

function ContextAccessor({
  vehicleDetails,
  vehicleServiceHistory,
  serviceRequest,
  selectedDealer,
}: BookingState): SelectDealerPageContext | null {
  if (!(vehicleDetails && vehicleServiceHistory && serviceRequest)) {
    return null;
  }

  return {
    vehicleServiceHistory,
    vehicleDetails,
    serviceRequest,
    selectedDealer,
  };
}

export const SelectDealerPage = withPageContext(ContextAccessor, ({ pageContext }) => {
  const viewProvider = useViewProvider();
  const [bookingState, setBookingContext] = useBookingContext();
  const [initialFormData] = useState<SelectDealerPageFormData>(() => ({
    dealerSearch: '',
    selectedDealerCode: pageContext.selectedDealer?.dealerCode ?? pageContext.vehicleServiceHistory?.dealerCode,
  }));
  const [preferredDealer, setPreferredDealer] = useState<Dealer>();
  const formRef = useRef<FormRef<SelectDealerPageFormData>>(null);
  const modalManager = useModalManager();
  const [isFirstDealerChange, setIsFirstDealerChange] = useState(true);

  const { data: dealers } = useGetDealers(['V', 'C'],{
    onSuccess: (data) => {
      const dealer = data.find((x) => x.dealerCode === initialFormData.selectedDealerCode);

      setPreferredDealer(dealer);
      setBookingContext({
        ...bookingState,
        foundDealersCount: data.length,
      })
    },
  });

  const isLoading = !dealers;

  const dealersWithoutPreferred = useMemo(
    () => dealers?.filter((x) => x.dealerCode !== preferredDealer?.dealerCode) ?? [],
    [preferredDealer, dealers],
  );

  const {
    result: searchedDealers,
    keyword: dealerSearchKeyword,
    search: searchDealers,
  } = useFuzzy<Dealer>(dealersWithoutPreferred, {
    keys: ['city', 'postalCode', 'address', 'name'],
    shouldSort: false,
  });

  const searchingBykeyword = useMemo(() => dealerSearchKeyword.length >= minCharsToSearching, [dealerSearchKeyword]);

  const {
    endIndex: displayedDealersCount,
    nextEnabled: displayMoreDealersEnabled,
    setNextPage: displayMoreDealers,
    setPage: setDisplayedDealersCount,
  } = usePagination({
    totalItems: searchedDealers?.length,
    initialPageSize: dealersPagination,
  });
  const displayedDealers = useMemo<Dealer[]>(
    () => searchedDealers.slice(0, displayedDealersCount + 1),
    [searchedDealers, displayedDealersCount],
  );

  // No price estimate is requested here. The Estimated Price step it fed was removed from the
  // wizard, yet up to three requests (DAL, DAL, the dealer's DMS) still ran on every Next and
  // kept the customer on this step whenever one of them failed. The hook that made them
  // (useFetchEstimation) and the backend endpoints behind it have since been removed; both
  // are in the git history if the step comes back.
  const proceedToNextStep = (selectedDealer: Dealer, nextStep: Function) => {
    const state: BookingState = {
      // The chosen date belongs to a dealer, so it is dropped when the dealer changes.
      ...(deepEqual(bookingState.selectedDealer, selectedDealer)
        ? bookingState
        : resetSelectServiceDatePageContext(bookingState)),
      selectedDealer,
    };

    setBookingContext(state);
    nextStep(state);
  };

  const handleDealerChange = (selectedDealerCode: string) => {
    const isDealerChanged = selectedDealerCode !== initialFormData.selectedDealerCode;

    if (isDealerChanged && isFirstDealerChange) {
      modalManager.open({
        title: 'Change Dealer',
        content: ModalContent,
        actions: [
          {
            title: 'Cancel',
            type: 'secondary',
            key: 'cancel',
            onClick(close) {
              // The dialog outlives the page: once the customer has navigated away, the form
              // is gone and there is nothing left to reset.
              formRef.current?.setValue('selectedDealerCode', initialFormData.selectedDealerCode);
              close();
            },
          },
          {
            title: 'Confirm Change',
            type: 'primary',
            key: 'confirm',
            onClick(close) {
              setIsFirstDealerChange(false);
              close();
            },
          },
        ],
      });
    }
  };

  const onNext: WizardNavigate = (nextStep) => {
    formRef.current!.submit((data) => {
      const selectedDealer = dealers?.find((x) => x.dealerCode === data.selectedDealerCode);

      // The preselected code comes from the service history and may belong to a dealer that is
      // not offered here (closed, other brand). Nothing is shown selected then, so the customer
      // has to pick a dealer — proceeding would carry an undefined dealer into the next steps.
      if (!selectedDealer) {
        return;
      }

      proceedToNextStep(selectedDealer, nextStep);
    });
  };
  const onPrevious: WizardNavigate = (previousStep) => {
    previousStep(bookingState);
  };
  const onSearchKeywordChange = (value: string): void => {
    searchDealers(value);
    setDisplayedDealersCount(0);
  };
  const onFormChanged = (): void => {
    const formValues = formRef.current!.getValues();

    if (dealerSearchKeyword !== formValues.dealerSearch) {
      const dealer = dealers?.find((x) => x.dealerCode === formValues.selectedDealerCode);

      setPreferredDealer(dealer);
    }
  };

  const viewProps: SelectDealerPageViewProps = {
    initialFormData,
    formRef,
    dealers: displayedDealers,
    preferredDealer,
    searchingBykeyword,
    noDealersFound: (searchingBykeyword && searchedDealers && searchedDealers.length === 0),
    loadMoreDealersEnabled: displayMoreDealersEnabled && searchingBykeyword,
    onLoadMoreDealers: displayMoreDealers,
    onSearchKeywordChange,
    onFormChanged,
    onDealerChange: handleDealerChange,
  };

  return (
    <Wizard currentStep={Steps.SelectDealer} onNext={onNext} onPrevious={onPrevious} disabled={isLoading}>
      {viewProvider.renderSelectDealerPage(viewProps)}
    </Wizard>
  );
});
